import {
    BUILDER_CHART_VIEW_TYPES,
    FavoriteLimits,
    MODAL_TYPE_HANDLER,
    PerpsAssetsData,
    PerpsBreakdownByCohorts,
    PerpsBreakdownBySize,
    PositionMetricsData,
    Positions,
    RANGE_TYPES,
    SegmentId,
    SegmentMetricsData,
    SegmentsBiasMetrics,
    SegmentsData,
    StartTotalWalletsEquity,
    StatsActivePerpTraders,
    StatsEquityDistributionBuckets,
    StatsExposeRatio,
    StatsMetricByHype,
    StatsMetrics,
    StatsTotalWalletsOnHL,
    StatsWalletCreationRate,
    TABLE_INTERACTION_TYPES,
    xUserType,
} from '@/lib/types/hypertrackerTypes';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { POSITION_DIRECTION } from '@/lib/constants/exchanges';

export type WalletOrderBy =
    | 'address'
    | 'totalEquity'
    | 'earliesActivityAt'
    | 'perpPnl'
    | 'perpEquity'
    | 'perpBias'
    | 'openValue'
    | 'sumUpnl'
    | 'closetLiqProgress'
    | 'countOpenPositions';

export type HeatmapOrderBy =
    | 'crossLeverage'
    | 'isolatedLeverage'
    | 'address'
    | 'value'
    | 'side'
    | 'size'
    | 'coin'
    | 'entryPrice'
    | 'unrealizedPnl'
    | 'funding'
    | 'liquidationPrice'
    | 'markPrice'
    | 'midPrice'
    | 'premiumPrice';

export type BuilderUsersFilters = 'address' | 'perpEquity' | 'perpPnl' | 'builderFee' | 'volume';

export interface SegmentParams {
    segmentId: number;
}

export interface LargestPositionsParams extends SegmentParams {
    limit: number;
    offset: number;
}

export interface WalletsParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    orderBy: WalletOrderBy;
    pnlSegmentIds: number[];
    sizeSegmentIds: number[];
    hasOpenPositions: boolean | undefined;
}

export interface HeatmapRequestParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    orderBy: HeatmapOrderBy;
    segmentIds: number[];
    coin: string;
}

export interface WalletRequestParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    orderBy: WalletOrderBy;
    segmentIds: number[];
    hasOpenPositions: boolean | undefined;
}

export interface UsersForBuilderParams {
    limit: number;
    offset: number;
    order: 'asc' | 'desc';
    orderBy: BuilderUsersFilters;
    period: RANGE_TYPES;
}

export interface LiquidationRiskParams extends LargestPositionsParams {}

export interface HypertrackerFilters {
    largestPositions: Omit<LargestPositionsParams, 'segmentId'>;
    wallets: Omit<WalletsParams, 'segmentId'>;
    liquidationRisk: Omit<LiquidationRiskParams, 'segmentId'>;
}

export interface WalletConnection {
    id: number;
    address: string;
    mid: number;
    emoji: string | null;
    displayName: string | null;
    createdAt: string;
    updatedAt: string;
    xUserId: string | number | null;
    cmmId: number;
    isUnlocked: boolean;
    privateName: string | null;
    xUser?: xUserType;
    perpMonthVolume: number;
    totalEquity: number;
    vault: boolean;
    leader: string | null;
    hypertracker?: any;
}

export interface VaultConnection {
    apr: number;
    cmmId: number;
    description: string;
    isClosed: true;
    leader: string;
    leaderCommission: number;
    leaderFraction: number;
    mid: number;
    name: string;
    tvl: number;
    vaultAddress: string;
    xUserId: string;
}

export interface VaultInfoModalDetails {
    leaderEquity: string;
    leaderCommission: string;
    description: string;
}

export interface PositionExplorerFilters {
    coin: string;
    limit: number;
    offset: number;
    order: 'desc' | 'asc';
    orderBy: HeatmapOrderBy;
    segmentIds: number[];
    valueRange: string[];
}

export interface LeaderboardsTableFilters {
    coin: string;
    limit: number;
    offset: number;
    order: 'desc' | 'asc';
    orderBy: any;
    segmentIds: number[];
    valueRange: string[];
    rankBy: string;
}

export interface LiquidationHeatmapItem {
    coin: string;
    liquidationValue: number;
    priceBinIndex: number;
    priceBinStart: number;
    priceBinEnd: number;
    positionsCount: number;
    liquidation?: number;
    roundPrice?: number;
    mostImpactedSegment: any;
}

export interface BuilderByPeriod {
    address: string;
    createdAt: string;
    refCode: string | null;
    revenue: number;
    timeJoined: string | null;
    usageFee: number;
    users: number;
    volume: number;
    revenuePrev?: number;
    usersPrev?: number;
    volumePrev?: number;
}

export interface Revenue {
    columns: string[];
    data: (string | number)[][];
    '24h'?: {
        uniqueUsers: number;
        revenue: number;
        timestamp: string;
    };
}

export interface BuildersByPeriod {
    builders: BuilderByPeriod[];
}

export interface TopBuildersRevenue {
    revenue: {
        allTime: Revenue;
        day: Revenue;
    };
    topBuilders: { address: string; refCode: string }[];
}

export interface RevenueByPeriods {
    ['7d']: Revenue;
    ['24h']: Revenue;
    ['30d']: Revenue;
    all: Revenue;
}

export interface BuilderUser {
    address: string;
    builderFee: number | null;
    closedPnl: number;
    equity: number | null;
    perpPnl: number;
    pnl: number | null;
    volume: number;
    wallet: {
        address: string;
        closestLiq: {
            coin: string | null;
            progress: number | null;
        };
        cmmId: number | string | null;
        displayName: string | null;
        earliestActivityAt: string;
        emoji: string | null;
        exposureRatio: number | null;
        favoriteCount: number;
        mid: string | number | null;
        openValue: number | null;
        perpBias: number | null;
        perpEquity: number;
        perpPnl: number;
        segments: SegmentId[];
        sumUpnl: number;
        totalEquity: number;
        vault: string | number | null;
        verified: boolean;
        xUserId: string | number | null;
        favorite?: {
            displayName?: string;
            emoji?: string;
            favorite?: boolean;
            featured?: boolean;
        };
    } | null;
}

export interface Analytics {
    countUsers: number;
    revenue: number;
    volume: number;
}

export interface AnalyticsByPeriod {
    ['1h']: Analytics;
    ['24h']: Analytics;
    ['7d']: Analytics;
    ['30d']: Analytics;
    ['all']: Analytics;
}
export interface PrevAnalyticsByPeriod {
    ['24h']: Analytics;
    ['7d']: Analytics;
    ['30d']: Analytics;
}

export interface BuilderV2Personal {
    analytics: RevenueByPeriods;
    biggestDay: {
        date: string;
        revenue: number;
    };
    coinVolumeDistribution: RevenueByPeriods;
    fee: number;
    hyperliquidVolume24h: number;
    info: {
        address: string;
        code: string;
        firstActivityAt: string;
        name: string;
        url: string | null;
        xUrl: string | null;
        xUser: {
            createdAt: string;
            data: {
                description: string;
                name: string;
                profileImageUrl: string;
                publicMetrics: {
                    followersCount: number;
                    followingCount: number;
                    listedCount: number;
                    tweetCount: number;
                };
                screenName: string;
                verified: boolean;
            };
            updatedAt: string;
            xUserId: string;
        };
        xUserId: string;
    };
    numProfitableUsers: {
        ['7d']: number;
        ['24h']: number;
        ['30d']: number;
        ['all']: number;
    };
    revenueMilestones: { date: string; done: boolean; label: string; revenue: number }[];
    totalAnalytics: AnalyticsByPeriod;
    totalAnalyticsPrev: PrevAnalyticsByPeriod;
    userVolumeDistribution: RevenueByPeriods;
    users: {
        ['24h']: BuilderUser[];
        ['7d']: BuilderUser[];
        ['30d']: BuilderUser[];
        ['all']: BuilderUser[];
    };
}

export interface UsersForBuilder {
    items: BuilderUser[];
    totalCount: number;
}

export interface BuilderRangeToggle {
    [key: string]: RANGE_TYPES;
}
export interface BuilderViewToggle {
    [key: string]: BUILDER_CHART_VIEW_TYPES;
}

export interface LeaderboardItemProfile {
    emoji: string;
    displayName: string;
    verified: boolean;
    xUser: xUserType;
    favorite: {
        favorite: boolean;
        displayName: string | null;
        emoji: string | null;
        featured: boolean;
    };
}

export interface LeaderboardDataItem {
    address: string;
    age: string;

    pnlDay?: number;
    pnlWeek?: number;
    pnlMonth?: number;
    pnlAllTime?: number;
    profile?: LeaderboardItemProfile;

    // pnl all
    totalValue?: number;
    stableValue?: number;
    topHolding?: string;

    // perp pnl
    perpEquity?: number;
    openValue?: number;
    openValueLong?: number;
    exposureRatio?: number;
    bias?: number;

    // avg daily perp pnl
    avgPnl?: number;
    greenDays?: number;
    volume?: number;
    highestPnl?: number;
}

export interface HypertrackerLeaderboardData {
    qualifiedWalletsCount: number;
    data: LeaderboardDataItem[];
}

export interface PositionProfile {
    address: string;
    totalEquity: number;
    perpEquity: number;
    countOpenPositions: number;
    pnl: number;
    balance: number;
    segments: number[];
    verified: boolean;
    displayName: string | null;
    emoji: string | null;
    xUserId: string | null;
}

export interface BiggestPositionsDataItem {
    id: string;
    side: POSITION_DIRECTION;
    address: string;
    value: number;
    size: number;
    coin: string;
    entryPrice: number;
    unrealizedPnl: number;
    funding: number;
    liquidationPrice: number;
    markPrice: number;
    midPrice: number;
    premiumPrice: number;
    crossLeverage: number;
    isolatedLeverage: number;
    closeTime: string | null;
    openTime: string;
    liquidationProgress: number;
    profile: PositionProfile;
}

export interface HypertrackerLeaderboardsBiggestPositions {
    activeAccountsCount: number;
    positionsCount: number;
    positions: BiggestPositionsDataItem[];
}

export enum LEADERBOARDS {
    PNL_PERP_ONLY = 'pnl_perp_only',
    ALL_PNL = 'all_pnl',
    AVG_DAILY_PERP_PNL = 'avg_daily_perp_pnl',
    BIGGEST_POSITIONS = 'biggest_positions',
    BIGGEST_30D = 'biggest_30_d',
    HIGHEST_EXPOSURE = 'highest_exposure',
    TOP_HYPE_HOLDERS = 'top_hype_holders',
    FAVORITES = 'favorites',
    TOP_CLAIMED = 'top_claimed',
}

export type HypertrackerLeaderboards = Record<
    LEADERBOARDS,
    HypertrackerLeaderboardData | HypertrackerLeaderboardsBiggestPositions
>;

export interface ModalWindow {
    modalType?: MODAL_TYPE_HANDLER | null;
    isTwitterData?: boolean;
    xUser?: xUserType | null;
    benefitModalType?: TABLE_INTERACTION_TYPES | null;
    authorizationModalType?: AUTH_TYPE | null;
    wallet?: string;
    positionId?: string;
    editConnection?: any;
    twitterConnectUrl?: string;
    editDisplayedNameForFavorites?: any;
    vaultInfoModalDetails?: VaultInfoModalDetails | null;
    [key: string]: any;
}

export interface HypertrackerState {
    segmentData: SegmentsData | null;
    segmentsSummary: SegmentsData | null;
    segmentsBiasMetrics: SegmentsBiasMetrics | null;
    singleSegmentSummary: Record<number, any>;
    largestPositions: Record<number, any>;
    wallets: Record<number, any>;
    liquidationRisk: Record<number, any>;
    filters: HypertrackerFilters;
    modalWindow: ModalWindow;
    isLoadingDisplayX: boolean;
    heatmapCellDetails: Positions[] | null;
    heatmapSelectedRow: string | null;
    heatmapSelectedField: number | null;
    tempKeyData: any;
    connections: WalletConnection[];
    vaults: VaultConnection[];
    favoritePositions: any[];
    favoriteAccounts: any[];
    favoriteIdsHolderObject: {
        [key: string]: { id: string; favorite: boolean; openTime: number | null };
    } | null;
    featuredAccounts: any[];
    featuredPositions: any[];
    favoriteLimits: FavoriteLimits;
    perpsCurrentAsset: string;
    perpsAssetsData: PerpsAssetsData | null;
    perpsPositionsToExplore: Array<string | number>;
    positionExplorerFilters: PositionExplorerFilters;
    buildersDailyAnalytics: Revenue | null;
    buildersAllTimeAnalytics: Revenue | null;
    buildersAllTime: BuildersByPeriod | null;
    builders24h: BuildersByPeriod | null;
    buildersByPeriod: BuildersByPeriod | null;
    topBuildersRevenue: TopBuildersRevenue | null;
    builderV2: BuilderV2Personal | null;
    usersForBuilder: UsersForBuilder | null;
    builderMainChartView: BuilderViewToggle | null;
    buildersTableRange: RANGE_TYPES;
    personalBuilderRange: BuilderRangeToggle | null;
    statsMetrics: StatsMetrics | null;
    statsExposeRatio: StatsExposeRatio | null;
    statsMetricByHype: StatsMetricByHype | null;
    statsActivePerpTraders: StatsActivePerpTraders | null;
    statsTotalWalletsOnHL: StatsTotalWalletsOnHL | null;
    statsWalletCreationRate: StatsWalletCreationRate | null;
    startTotalWalletsEquity: StartTotalWalletsEquity | null;
    statsEquityDistributionBuckets: StatsEquityDistributionBuckets | null;
    leaderboards: HypertrackerLeaderboards | null;
    leaderboardsTableFilters: LeaderboardsTableFilters;
}
